import { useEffect, useState } from 'react';
import { InfoTip } from './InfoTip';
import { PANEL_INFO } from '../panelInfo';

interface FedData {
  updatedAt: string;
  policy: {
    targetLow: number | null;
    targetHigh: number | null;
    effective: number | null;
    effectiveDate: string | null;
    spark1y: number[];
  } | null;
  inflation: {
    cpiYoY: number | null;
    coreCpiYoY: number | null;
    pceYoY: number | null;
    cpiPrevYoY: number | null;
    asOf: string | null;
  } | null;
  labor: {
    unemployment: number | null;
    unemploymentPrev: number | null;
    payrollsChangeK: number | null;
    asOf: string | null;
  } | null;
  curve: {
    spread2s10s: number | null;
    tenYear: number | null;
    asOf: string | null;
  } | null;
  balanceSheet: {
    totalTrillions: number | null;
    change3moBillions: number | null;
    asOf: string | null;
  } | null;
  nextFomc: { date: string; daysAway: number } | null;
  expectations: {
    effective: number | null;
    y3mo: number | null;
    y1: number | null;
    y2: number | null;
    impliedBp12mo: number | null;
    impliedQuarterMoves: number | null;
    nearTerm: 'cut' | 'hold' | 'hike' | null;
    mood: 'dovish' | 'neutral' | 'hawkish' | null;
  } | null;
}

const MOOD_COLOR = {
  dovish: 'var(--green)',
  neutral: 'var(--text-dim)',
  hawkish: 'var(--amber)',
} as const;

const month = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—';

/**
 * UTC timestamp for a given hour-of-day in New York on a given date —
 * FOMC decisions drop at 14:00 ET, and ET is UTC-4 or UTC-5 depending on DST.
 */
function etTimestamp(dateStr: string, hourEt: number): number {
  const noonUtc = new Date(`${dateStr}T12:00:00Z`);
  const etHourAtNoonUtc = parseInt(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      hour12: false,
    }).format(noonUtc),
    10
  );
  const offset = 12 - (etHourAtNoonUtc % 24); // hours ET lags UTC (4 in summer, 5 in winter)
  return new Date(`${dateStr}T12:00:00Z`).getTime() + (hourEt + offset - 12) * 3600000;
}

function FomcCountdown({ date }: { date: string }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const target = etTimestamp(date, 14); // 2:00 PM ET decision
  const ms = target - now;

  if (ms <= 0 && ms > -3 * 3600000) {
    return <div className="btc-price" style={{ color: 'var(--amber)' }}>DECISION OUT</div>;
  }
  if (ms <= 0) {
    return <div className="btc-price">—</div>;
  }

  const days = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const two = (n: number) => String(n).padStart(2, '0');

  return (
    <div className="btc-price fomc-countdown" style={{ color: days === 0 ? 'var(--amber)' : undefined }}>
      {days > 0 ? `${days}d ` : ''}
      {two(h)}:{two(m)}:{two(s)}
    </div>
  );
}

function sparkPath(vals: number[], w = 100, h = 32): string {
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  return (
    'M' +
    vals
      .map((v, i) => {
        const x = (i / (vals.length - 1)) * w;
        const y = h - 2 - ((v - min) / span) * (h - 4);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' L')
  );
}

export function FedSection() {
  const [data, setData] = useState<FedData | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch('/api/fed');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setData(await r.json());
        setError(false);
      } catch {
        setError(true);
      }
    };
    load();
    const interval = setInterval(load, 1800000); // macro moves slowly — 30min
    return () => clearInterval(interval);
  }, []);

  if (error && !data) return null;
  const pol = data?.policy ?? null;
  const inf = data?.inflation ?? null;
  const lab = data?.labor ?? null;
  const cur = data?.curve ?? null;
  const bs = data?.balanceSheet ?? null;

  return (
    <div className="panel bitcoin-section" id="fed">
      <div className="panel-header">
        <div className="panel-title">
          <h2>🏛 Fed</h2>
          <InfoTip paragraphs={PANEL_INFO.fed} />
        </div>
        <span className="badge">
          {pol?.targetLow != null && pol?.targetHigh != null
            ? `target ${pol.targetLow.toFixed(2)}–${pol.targetHigh.toFixed(2)}%`
            : 'FRED'}
        </span>
      </div>

      {!data ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="fed-grid">
          {/* Policy rate */}
          {pol && (
            <div className="btc-card fed-card-wide">
              <h3 className="btc-card-title">Policy Rate</h3>
              <div className="btc-price">
                {pol.targetLow != null && pol.targetHigh != null
                  ? `${pol.targetLow.toFixed(2)}–${pol.targetHigh.toFixed(2)}%`
                  : pol.effective != null
                    ? `${pol.effective.toFixed(2)}%`
                    : '—'}
              </div>
              {pol.spark1y.length > 3 && (
                <svg className="btc-spark" viewBox="0 0 100 32" preserveAspectRatio="none">
                  <path
                    d={sparkPath(pol.spark1y)}
                    fill="none"
                    stroke="var(--green-mid)"
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              )}
              <div className="btc-rows btc-dim">
                <span>
                  effective {pol.effective != null ? `${pol.effective.toFixed(2)}%` : '—'}
                </span>
                <span>1y trend</span>
              </div>
            </div>
          )}

          {/* Inflation */}
          {inf && (
            <div className="btc-card">
              <h3 className="btc-card-title">Inflation (YoY)</h3>
              <div
                className="btc-price"
                style={{ color: (inf.cpiYoY ?? 0) > 3 ? 'var(--amber)' : 'var(--text)' }}
              >
                {inf.cpiYoY != null ? `${inf.cpiYoY.toFixed(1)}%` : '—'}
                <span className="btc-dim" style={{ fontSize: 11, marginLeft: 6 }}>CPI</span>
              </div>
              <div className="btc-rows">
                <span>
                  core <b>{inf.coreCpiYoY != null ? `${inf.coreCpiYoY.toFixed(1)}%` : '—'}</b>
                </span>
                <span>
                  PCE <b>{inf.pceYoY != null ? `${inf.pceYoY.toFixed(1)}%` : '—'}</b>
                </span>
              </div>
              <div className="btc-rows btc-dim">
                <span>
                  {inf.cpiYoY != null && inf.cpiPrevYoY != null
                    ? inf.cpiYoY > inf.cpiPrevYoY
                      ? 'heating vs last month'
                      : inf.cpiYoY < inf.cpiPrevYoY
                        ? 'cooling vs last month'
                        : 'flat vs last month'
                    : ''}{' '}
                  · target 2% · as of {month(inf.asOf)}
                </span>
              </div>
            </div>
          )}

          {/* Labor */}
          {lab && (
            <div className="btc-card">
              <h3 className="btc-card-title">Labor</h3>
              <div className="btc-price">
                {lab.unemployment != null ? `${lab.unemployment.toFixed(1)}%` : '—'}
                <span className="btc-dim" style={{ fontSize: 11, marginLeft: 6 }}>unemployment</span>
              </div>
              <div className="btc-rows">
                <span>
                  payrolls{' '}
                  <b style={{ color: (lab.payrollsChangeK ?? 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {lab.payrollsChangeK != null
                      ? `${lab.payrollsChangeK >= 0 ? '+' : ''}${Math.round(lab.payrollsChangeK)}k`
                      : '—'}
                  </b>
                  <span className="btc-dim"> last month</span>
                </span>
              </div>
              <div className="btc-rows btc-dim">
                <span>
                  {lab.unemployment != null && lab.unemploymentPrev != null
                    ? lab.unemployment > lab.unemploymentPrev
                      ? `up from ${lab.unemploymentPrev.toFixed(1)}%`
                      : lab.unemployment < lab.unemploymentPrev
                        ? `down from ${lab.unemploymentPrev.toFixed(1)}%`
                        : 'unchanged'
                    : ''}{' '}
                  · as of {month(lab.asOf)}
                </span>
              </div>
            </div>
          )}

          {/* Yield curve */}
          {cur && (
            <div className="btc-card">
              <h3 className="btc-card-title">Yield Curve</h3>
              <div
                className="btc-price"
                style={{ color: (cur.spread2s10s ?? 0) < 0 ? 'var(--red)' : 'var(--green)' }}
              >
                {cur.spread2s10s != null
                  ? `${cur.spread2s10s >= 0 ? '+' : ''}${(cur.spread2s10s * 100).toFixed(0)}bp`
                  : '—'}
                <span className="btc-dim" style={{ fontSize: 11, marginLeft: 6 }}>2s10s</span>
              </div>
              <div className="btc-rows">
                <span>
                  10Y <b>{cur.tenYear != null ? `${cur.tenYear.toFixed(2)}%` : '—'}</b>
                </span>
              </div>
              <div className="btc-rows btc-dim">
                <span>
                  {cur.spread2s10s != null
                    ? cur.spread2s10s < 0
                      ? 'inverted — classic recession signal'
                      : 'normal slope'
                    : ''}
                </span>
              </div>
            </div>
          )}

          {/* Balance sheet */}
          {bs && (
            <div className="btc-card">
              <h3 className="btc-card-title">Balance Sheet</h3>
              <div className="btc-price">
                {bs.totalTrillions != null ? `$${bs.totalTrillions.toFixed(2)}T` : '—'}
              </div>
              <div className="btc-rows">
                <span>
                  3mo{' '}
                  <b style={{ color: (bs.change3moBillions ?? 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {bs.change3moBillions != null
                      ? `${bs.change3moBillions >= 0 ? '+' : ''}$${Math.round(bs.change3moBillions)}B`
                      : '—'}
                  </b>
                </span>
              </div>
              <div className="btc-rows btc-dim">
                <span>
                  {bs.change3moBillions != null
                    ? bs.change3moBillions < -10
                      ? 'QT — draining liquidity'
                      : bs.change3moBillions > 10
                        ? 'expanding — adding liquidity'
                        : 'roughly flat'
                    : ''}
                </span>
              </div>
            </div>
          )}

          {/* Rate expectations — market-implied mood */}
          {data.expectations && (
            <div className="btc-card card-with-tip">
              <h3 className="btc-card-title">
                Rate Expectations
                <InfoTip
                  paragraphs={[
                    'HAWKISH = the market expects the Fed to RAISE rates (or keep them high). A hawk fights inflation with expensive money — usually a headwind for stocks and crypto. DOVISH = the market expects rate CUTS: cheaper money, usually a tailwind for risk assets. NEUTRAL = the Fed is expected to hold.',
                    'This is what the MARKET is pricing, not what the Fed has said. Read: if the 1-year Treasury yields more than today’s overnight rate, investors are betting rates go up (hawkish); if it yields less, they expect cuts (dovish). "12mo priced" converts that gap into basis points and quarter-point moves; "near-term" applies the same logic to the 3-month bill.',
                  ]}
                />
              </h3>
              <div
                className="btc-price"
                style={{ color: MOOD_COLOR[data.expectations.mood ?? 'neutral'] }}
              >
                {(data.expectations.mood ?? 'neutral').toUpperCase()}
              </div>
              {data.expectations.effective !== null && data.expectations.impliedBp12mo !== null && (
                <div className="btc-rows fed-rate-path">
                  <span>
                    now <b>{data.expectations.effective.toFixed(2)}%</b>
                    <span className="btc-dim"> → </span>12mo{' '}
                    <b style={{ color: MOOD_COLOR[data.expectations.mood ?? 'neutral'] }}>
                      ~
                      {(
                        data.expectations.effective +
                        data.expectations.impliedBp12mo / 100
                      ).toFixed(2)}
                      %
                    </b>
                  </span>
                </div>
              )}
              {data.expectations.impliedBp12mo !== null && (
                <div className="btc-rows">
                  <span>
                    12mo priced:{' '}
                    <b style={{ color: MOOD_COLOR[data.expectations.mood ?? 'neutral'] }}>
                      {data.expectations.impliedBp12mo >= 0 ? '+' : ''}
                      {Math.round(data.expectations.impliedBp12mo)}bp
                    </b>
                    <span className="btc-dim">
                      {' '}
                      (≈{Math.abs(Math.round(data.expectations.impliedQuarterMoves ?? 0))}{' '}
                      {data.expectations.impliedBp12mo < 0 ? 'cuts' : 'hikes'})
                    </span>
                  </span>
                </div>
              )}
              <div className="btc-rows">
                <span>
                  near-term:{' '}
                  <b
                    style={{
                      color:
                        data.expectations.nearTerm === 'cut'
                          ? 'var(--green)'
                          : data.expectations.nearTerm === 'hike'
                            ? 'var(--amber)'
                            : 'var(--text)',
                    }}
                  >
                    {data.expectations.nearTerm ?? '—'}
                  </b>
                </span>
                <span className="btc-dim">
                  1Y {data.expectations.y1?.toFixed(2)}% · 2Y {data.expectations.y2?.toFixed(2) ?? '—'}%
                </span>
              </div>
              <div className="btc-rows btc-dim">
                <span>implied from Treasury curve vs effective rate</span>
              </div>
            </div>
          )}

          {/* Next FOMC */}
          {data.nextFomc && (
            <div className="btc-card">
              <h3 className="btc-card-title">Next FOMC</h3>
              <FomcCountdown date={data.nextFomc.date} />
              <div className="btc-rows btc-dim">
                <span>
                  {new Date(`${data.nextFomc.date}T12:00:00Z`).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}{' '}
                  · decision 2:00 PM ET
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
